import { Injectable } from "@angular/core";
import {
  ApiAction,
  ApiAddAction,
  ApiBrowseAction,
  ApiDeleteAction,
  ApiEditAction
} from "../../api-actions";
import { ApiStateStoreService } from "../../api-state-store.service";
import { ApiSessionService } from "../../api-session.service";
import { organizationId } from "../../apiParams/organization-id";
import { sessionId } from "../../apiParams/session-id";
import { APIDid, Did, apiDidToDid as clean } from "./did";
import { extractData, OnsipApiResponse } from "../../apiResponse/response-body-new";
import { getApiActionName } from "../../onsip-api-action-new";
import { ParameterValue } from "../../util/api-action-description";
import { ApiResourceService } from "../api-resource.service";
import { arrayToRecord } from "../../util/arrayToRecord";
import { onsipApiArrayToArray } from "../../apiResponse/xml-json";
import { RequireAtLeastOne } from "../../../../interfaces/requireAtLeastOneType";
import { ApiPromiseState, ApiPromiseStateService } from "../../api-promise-state.service";
import { PoolDid } from "./pool-did";
import { Observable } from "rxjs";
import { map, withLatestFrom } from "rxjs/operators";
import { currentOrgUserId } from "../../apiParams/current-organization-user-id";
export { Did };

const debug = false;

@Injectable({ providedIn: "root" })
export class DidService extends ApiResourceService<Did> {
  constructor(
    session: ApiSessionService,
    store: ApiStateStoreService,
    promiseState: ApiPromiseStateService
  ) {
    // indexing by number because didId can be modified upon reassignment of phone number from one user to another
    super(session, store, promiseState, "Did", "number");
    debug && this.state.subscribe(state => console.warn("DidService", state));
  }

  // getter would be needed when we would have several PBXes
  get currentOrgDids(): Observable<Array<Did>> {
    return this.state.pipe(
      withLatestFrom(this.store.state.pipe(organizationId())),
      map(([substate, selfOrganizationId]) =>
        Object.values(substate.state).filter(
          ({ organizationId }) => organizationId === selfOrganizationId
        )
      )
    );
  }

  didBrowse(extraParameters?: Record<string, ParameterValue>): ApiPromiseState<Did> {
    this.dispatcher.next({
      parameters: {
        Action: ApiBrowseAction.DidBrowse,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        Limit: 1000, // not sure what limit we should go for
        ...extraParameters
      }
    });
    return this.promiseState.toPromise(ApiBrowseAction.DidBrowse);
  }

  didAssign(
    DidId: string,
    Destination: string,
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<Did> {
    this.dispatcher.next({
      parameters: {
        Action: ApiEditAction.DidEdit,
        CallerIdNumberRequired: false,
        Destination,
        DidId,
        SessionId: this.store.state.pipe(sessionId()),
        UserId: this.store.state.pipe(currentOrgUserId()),
        ...extraParameters
      }
    });
    return this.promiseState.toPromise(ApiEditAction.DidEdit);
  }

  didEditNote(
    DidId: string,
    Note: string,
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<Did> {
    this.dispatcher.next({
      parameters: {
        Action: ApiEditAction.DidEditNote,
        DidId,
        Note,
        SessionId: this.store.state.pipe(sessionId()),
        ...extraParameters
      }
    });
    return this.promiseState.toPromise(ApiEditAction.DidEditNote);
  }

  /** NOTE delete is a different response type, PoolDid */
  didDelete(
    DidId: string,
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<PoolDid> {
    this.dispatcher.next({
      parameters: {
        Action: ApiDeleteAction.DidDelete,
        DidId,
        SessionId: this.store.state.pipe(sessionId()),
        ...extraParameters
      }
    });
    return this.promiseState.toPromise(ApiDeleteAction.DidDelete);
  }

  didRequest(
    Npa: string,
    Nxx: string,
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<Did> {
    const parameters: any = {
      Action: ApiAddAction.DidRequest,
      Protocol: "sip", // iax is legacy i think
      SessionId: this.store.state.pipe(sessionId()),
      UserId: this.store.state.pipe(currentOrgUserId()),
      ...extraParameters
    };
    parameters[`NpaNxx[${Npa}${Nxx}]`] = 1;
    this.dispatcher.next({
      parameters: parameters
    });
    return this.promiseState.toPromise(ApiAddAction.DidRequest);
  }

  didRequestWithoutFill(
    params: { Npa: string; Quantity: number; Nxx?: string },
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<Did> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.DidRequestWithoutFill,
        SessionId: this.store.state.pipe(sessionId()),
        UserId: this.store.state.pipe(currentOrgUserId()),
        ...params,
        ...extraParameters
      }
    });
    return this.promiseState.toPromise(ApiAction.DidRequestWithoutFill);
  }

  didRequestFillPoolDid(
    params: RequireAtLeastOne<{
      PoolDidIds: Array<string> | undefined;
      FlowrouteNumbers: Array<string> | undefined;
    }>,
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<Did> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.DidRequestFillPoolDid,
        Protocol: "sip",
        SessionId: this.store.state.pipe(sessionId()),
        UserId: this.store.state.pipe(currentOrgUserId()),
        ...params,
        ...extraParameters
      }
    });
    return this.promiseState.toPromise(ApiAction.DidRequestFillPoolDid);
  }

  didEditName(
    DidId: string,
    Name: string,
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<Did> {
    this.dispatcher.next({
      parameters: {
        Action: ApiEditAction.DidEditName,
        DidId,
        Name,
        SessionId: this.store.state.pipe(sessionId()),
        ...extraParameters
      }
    });
    return this.promiseState.toPromise(ApiEditAction.DidEditName);
  }

  // It is the same api call but with the destination set to "sip.jnctn.net"
  // Making a separate function for better interface at the component level
  didUnassign(
    DidId: string,
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<Did> {
    this.dispatcher.next({
      parameters: {
        Action: ApiEditAction.DidEdit,
        CallerIdNumberRequired: false,
        Destination: "sip.jnctn.net",
        DidId,
        SessionId: this.store.state.pipe(sessionId()),
        UserId: this.store.state.pipe(currentOrgUserId()),
        ...extraParameters
      },
      nonblocking: true
    });
    return this.promiseState.toPromise(ApiEditAction.DidEdit);
  }

  reducer(response: OnsipApiResponse): void {
    const action = getApiActionName(response);
    switch (action) {
      case ApiBrowseAction.DidBrowse:
      case ApiAddAction.DidRequest:
      case ApiAction.DidRequestFillPoolDid:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            extractData<Array<APIDid>>(response, action, "Did", "Dids").map(clean),
            this.indexKeyName
          ),
          action
        );
        break;
      case ApiEditAction.DidEdit:
      case ApiEditAction.DidEditNote:
      case ApiEditAction.DidEditName:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord([clean(extractData<APIDid>(response, action, "Did"))], this.indexKeyName),
          action
        );
        break;
      /** NOTE delete is a different response type, PoolDid */
      case ApiDeleteAction.DidDelete:
        if (!response.Context.Request.Parameters) break;
        // eslint-disable-next-line no-case-declarations
        const deleted = onsipApiArrayToArray(response.Context.Request.Parameters, "Parameter").find(
          param => param.Name === "Number"
        )?.Value;
        deleted && this.store.mergeStateUpdate(this.resourceName, { [deleted]: undefined }, action);
        break;
    }
  }
}
